import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, Info, Eye, CheckCircle2, Clock } from 'lucide-react';
import { buildRubricContext } from '@/data/enterpriseDiagnosticData';
import DiagnosticRubricsEditor from './DiagnosticRubricsEditor';
import type { RubricsOverride } from './DiagnosticRubricsEditor';

const AI_MODELS = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (rápido, económico)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (alta calidad)' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (ultra rápido)' },
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash Preview' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini (equilibrado)' },
  { value: 'openai/gpt-5', label: 'GPT-5 (máxima calidad)' },
];

const DEFAULT_SYSTEM_PROMPT = `Eres un evaluador experto del marco de madurez LIDER-IA para organizaciones.

Tu tarea es evaluar las respuestas de un diagnóstico empresarial contra las rúbricas de calificación del marco LIDER-IA.

## Rúbricas de Evaluación por Subdimensión

{{RUBRIC_CONTEXT}}

## Instrucciones de Evaluación

1. Para cada subdimensión (L1, L2, L3, L4, I1, I2, I3, D1, D2, D3, E1, E2, E3, R1, R2), evalúa las respuestas del participante contra los criterios de la rúbrica.
2. Asigna un score de 1 a 5 basándote en la EVIDENCIA proporcionada en las respuestas, no en la cantidad de texto.
3. Si el participante marca "No puede responder" en todas las preguntas de una subdimensión, asigna nivel 1.
4. El score de cada dimensión (L, I, D, E, R) es el PROMEDIO de sus subdimensiones, redondeado al entero más cercano.
5. Proporciona una justificación breve para cada subdimensión.

RESPONDE ÚNICAMENTE en formato JSON válido con esta estructura exacta:
{
  "dimensions": [
    {
      "dimensionId": "L",
      "name": "Liderazgo y Estrategia",
      "score": 3,
      "subDimensionScores": [
        { "subDimensionId": "L1", "subDimensionName": "Visión y Objetivos", "score": 3, "justification": "..." },
        { "subDimensionId": "L2", "subDimensionName": "Principios Rectores", "score": 2, "justification": "..." },
        { "subDimensionId": "L3", "subDimensionName": "Contexto, Políticas y Alineación", "score": 3, "justification": "..." },
        { "subDimensionId": "L4", "subDimensionName": "Gobierno y ROI", "score": 2, "justification": "..." }
      ]
    },
    ... (las 5 dimensiones L, I, D, E, R)
  ]
}`;

interface DiagnosticConfig {
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
}

const DEFAULT_CONFIG: DiagnosticConfig = {
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  model: 'google/gemini-2.5-flash',
  temperature: 0.2,
  max_tokens: 4000,
};

const SETTINGS_KEY = 'diagnostic_prompt_config';

const PLACEHOLDERS = [
  { key: '{{RUBRIC_CONTEXT}}', desc: 'Rúbricas de evaluación del marco LIDER-IA (15 subdimensiones, 5 niveles cada una)' },
];

export default function DiagnosticConfigTab() {
  const qc = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: savedRow, isLoading } = useQuery({
    queryKey: ['platform_settings', SETTINGS_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('key', SETTINGS_KEY)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: rubricsRow } = useQuery({
    queryKey: ['platform_settings', 'diagnostic_rubrics_override'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('key', 'diagnostic_rubrics_override')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const rubricsOverride = rubricsRow ? (rubricsRow.value as unknown as RubricsOverride) : undefined;

  const savedConfig = savedRow ? (savedRow.value as unknown as DiagnosticConfig) : null;
  const isSaved = !!savedConfig;
  const lastUpdated = savedRow?.updated_at;
  const activeDefaults = savedConfig || DEFAULT_CONFIG;
  const [form, setForm] = useState<DiagnosticConfig | null>(null);
  const active = form || activeDefaults;

  const saveMutation = useMutation({
    mutationFn: async (config: DiagnosticConfig) => {
      const { data: existing } = await supabase
        .from('platform_settings')
        .select('id')
        .eq('key', SETTINGS_KEY)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('platform_settings')
          .update({ value: config as any })
          .eq('key', SETTINGS_KEY);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('platform_settings')
          .insert({ key: SETTINGS_KEY, value: config as any });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', SETTINGS_KEY] });
      setForm(null);
      toast({ title: 'Configuración del diagnóstico empresarial guardada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const rubricPreview = buildRubricContext(rubricsOverride);
  const EXAMPLE_PREVIEW = active.system_prompt.replace('{{RUBRIC_CONTEXT}}', rubricPreview);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Prompt de Diagnóstico Empresarial IA</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configura el prompt que usa la IA para evaluar la madurez organizacional con el marco LIDER-IA (5 dimensiones, 15 subdimensiones, escala 1-5).
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isSaved ? (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Guardada
                </Badge>
              ) : (
                <Badge variant="warning" className="gap-1">
                  <Clock className="w-3 h-3" /> Por defecto
                </Badge>
              )}
            </div>
          </div>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground">
              Última actualización: {new Date(lastUpdated).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>System Prompt</Label>
            <Textarea
              className="min-h-[250px] mt-1 font-mono text-xs"
              value={active.system_prompt}
              onChange={(e) => setForm(prev => ({ ...(prev || activeDefaults), system_prompt: e.target.value }))}
              placeholder="Instrucciones para el evaluador IA del diagnóstico empresarial..."
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
            <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Variables disponibles en el prompt</p>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map(p => (
                  <div key={p.key} className="flex items-center gap-1.5">
                    <Badge variant="secondary" className="font-mono text-xs">{p.key}</Badge>
                    <span className="text-xs text-muted-foreground">{p.desc}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Nota: La información de empresa (nombre, sector) y las respuestas del diagnóstico se envían automáticamente como mensaje del usuario.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Modelo</Label>
              <Select
                value={active.model}
                onValueChange={(v) => setForm(prev => ({ ...(prev || activeDefaults), model: v }))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Temperatura: {active.temperature}</Label>
              <Slider
                className="mt-3"
                min={0}
                max={1}
                step={0.1}
                value={[active.temperature]}
                onValueChange={([v]) => setForm(prev => ({ ...(prev || activeDefaults), temperature: v }))}
              />
            </div>

            <div>
              <Label>Max tokens</Label>
              <Input
                type="number"
                className="mt-1"
                value={active.max_tokens}
                onChange={(e) => setForm(prev => ({ ...(prev || activeDefaults), max_tokens: parseInt(e.target.value) || 4000 }))}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            {form && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-1" />
                  {saveMutation.isPending ? 'Guardando...' : 'Guardar configuración'}
                </Button>
              </div>
            )}
            <Button variant="outline" size="sm" className="gap-1.5 ml-auto" onClick={() => setPreviewOpen(true)}>
              <Eye className="w-4 h-4" /> Ver preview del prompt
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview del System Prompt — Diagnóstico Empresarial</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">Modelo: {active.model}</Badge>
              <Badge variant="secondary">Temp: {active.temperature}</Badge>
              <Badge variant="secondary">Max tokens: {active.max_tokens}</Badge>
            </div>
            <pre className="text-xs bg-muted/50 p-4 rounded-lg border border-border whitespace-pre-wrap font-mono leading-relaxed text-foreground">
              {EXAMPLE_PREVIEW}
            </pre>
          </div>
        </DialogContent>
      </Dialog>

      <DiagnosticRubricsEditor />
    </div>
  );
}
